import React from 'react'
import { UserPlus } from 'lucide-react'
import { useUserManagement } from '@/hooks/useUserManagement'
import UserFilters from './components/UserFilters'
import UserTable from './components/UserTable'
import CreateUserModal from './components/CreateUserModal'
import ConfirmDeleteModal from './components/ConfirmDeleteModal'
import EditUserModal from '../EditUserModal'
import PendingUsers from '@/components/admin/PendingUsers'

const UserManagement: React.FC = () => {
  const {
    // Data
    users,
    pagination,
    
    // UI State
    loading,
    error,
    
    // Filter State
    searchTerm,
    roleFilter,
    
    // Modal State
    showCreateModal,
    editingUser,
    deleteConfirm,
    
    // Actions
    setSearchTerm,
    setRoleFilter,
    setCurrentPage,
    setError,
    
    // Modal Actions
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openDeleteModal,
    closeDeleteModal,
    
    // Data Actions
    refreshUsers,
    deleteUser
  } = useUserManagement()

  return (
    <div className="space-y-8">
      {/* Pending Users Section */}
      <PendingUsers onUserApproved={refreshUsers} />

      {/* Active Users Section */}
      <div className="space-y-6">
        {/* Header with actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-medium text-foreground">Active Users</h3>
            <p className="text-sm text-muted-foreground">
              Manage user accounts, roles, and permissions ({pagination.total} users)
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            Add User
          </button>
        </div>

      {/* Filters and Search */}
      <UserFilters
        searchTerm={searchTerm}
        roleFilter={roleFilter}
        onSearchChange={setSearchTerm}
        onRoleFilterChange={setRoleFilter}
      />

      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Users Table */}
      <UserTable
        users={users}
        loading={loading}
        searchTerm={searchTerm}
        roleFilter={roleFilter}
        pagination={pagination}
        onEditUser={openEditModal}
        onDeleteUser={openDeleteModal}
        onPageChange={setCurrentPage}
      />

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={closeCreateModal}
        onSuccess={refreshUsers}
        onError={setError}
      />

      {/* Edit User Modal */}
      <EditUserModal
        user={editingUser}
        isOpen={!!editingUser}
        onClose={closeEditModal}
        onSuccess={refreshUsers}
        onError={setError}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        user={deleteConfirm}
        isOpen={!!deleteConfirm}
        onConfirm={() => deleteConfirm && deleteUser(deleteConfirm)}
        onCancel={closeDeleteModal}
      />
      </div>
    </div>
  )
}

export default UserManagement 